import { useEffect, useState } from "react"
import { router, useForm } from "@inertiajs/react"
import { Loader2 } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"

// `member` describes which entity to edit; the dialog body always edits
// Account profile fields (the same set is valid for both modes).
//
// `member.mode` controls the PATCH target:
//   - "membership" (default): `member.id` is a group_membership id; PATCH
//     /app/coach/group/members/:id (existing GroupController#update_member).
//   - "account": `member.id` is an account id; PATCH /app/coach/members/:id
//     (MembersController#update). Used for guests/prospects who are not
//     members of any coached group, since they have no membership row.
//
// Email (task #650) is NOT part of the profile PATCH — the server drops it
// there, because the email is the login identity. A changed email goes to a
// second request, POST /app/coach/members/:accountId/email_change, which
// changes it at once. Before that, a confirm modal shows the old and new
// address so the coach can check for a typo.
export function EditMemberDialog({ member, onClose }) {
  const form = useForm({
    name: "",
    email: "",
    job_title: "",
    company: "",
    bio: "",
    name_phonetic: "",
    city: "",
    state: "",
    country: "",
  })

  const memberId = member?.id
  const mode = member?.mode || "membership"
  const accountId = member?.accountId ?? (mode === "account" ? member?.id : null)
  const [confirmEmailOpen, setConfirmEmailOpen] = useState(false)

  // Reset on every open (memberId transitions null -> id) so reopening
  // the same member after a Cancel shows fresh server data, not the
  // discarded previous edits. The dialog is mounted persistently on the
  // page; without keying off both the open transition and memberId, the
  // form state would carry across opens.
  useEffect(() => {
    if (member) {
      form.setData({
        name: member.rawName ?? "",
        email: member.email ?? "",
        job_title: member.jobTitle ?? "",
        company: member.company ?? "",
        bio: member.bio ?? "",
        name_phonetic: member.namePhonetic ?? "",
        city: member.city ?? "",
        state: member.state ?? "",
        country: member.country ?? "",
      })
      form.clearErrors()
      setConfirmEmailOpen(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memberId])

  const newEmail = form.data.email.trim()
  const emailChanged =
    !!accountId && !!member && newEmail.toLowerCase() !== (member.email ?? "").toLowerCase()

  // Second step of Save: only when the email field differs from the
  // current email, so a name-only save never mails the member. On an error
  // the dialog stays open with the message under the email field.
  const changeEmail = () => {
    router.post(
      `/app/coach/members/${accountId}/email_change`,
      { new_email: newEmail },
      {
        preserveScroll: true,
        preserveState: true,
        onSuccess: (page) => {
          if (page.props.errors?.new_email) return
          onClose()
        },
        onError: (errors) => form.setError("email", errors.new_email),
      },
    )
  }

  const save = () => {
    const url =
      mode === "account"
        ? `/app/coach/members/${member.id}`
        : `/app/coach/group/members/${member.id}`
    form.transform(({ email: _email, ...profile }) => profile)
    form.patch(url, {
      preserveScroll: true,
      onSuccess: () => (emailChanged ? changeEmail() : onClose()),
    })
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    if (!member) return
    if (emailChanged) {
      setConfirmEmailOpen(true)
    } else {
      save()
    }
  }

  return (
    <Dialog open={!!member} onOpenChange={(o) => { if (!o) onClose() }}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit {member?.name || "member"}</DialogTitle>
          <DialogDescription>
            Update member information. Changes save to their profile.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Name</Label>
              <Input
                value={form.data.name}
                onChange={(e) => form.setData("name", e.target.value)}
              />
              {form.errors.name && <p className="text-xs text-red-600">{form.errors.name}</p>}
            </div>

            {accountId && (
              <div className="space-y-1.5">
                <Label htmlFor="edit-member-email">Email</Label>
                <Input
                  id="edit-member-email"
                  type="email"
                  value={form.data.email}
                  onChange={(e) => form.setData("email", e.target.value)}
                />
                {form.errors.email && <p className="text-xs text-red-600">{form.errors.email}</p>}
              </div>
            )}

            <div className="space-y-1.5">
              <Label>Job title</Label>
              <Input
                value={form.data.job_title}
                onChange={(e) => form.setData("job_title", e.target.value)}
              />
            </div>

            <div className="space-y-1.5">
              <Label>Company</Label>
              <Input
                value={form.data.company}
                onChange={(e) => form.setData("company", e.target.value)}
              />
            </div>

            <div className="space-y-1.5">
              <Label>Phonetic name</Label>
              <Input
                value={form.data.name_phonetic}
                onChange={(e) => form.setData("name_phonetic", e.target.value)}
                placeholder="e.g. JOR-dan"
              />
            </div>

            <div className="space-y-1.5">
              <Label>Country</Label>
              <Input
                value={form.data.country}
                onChange={(e) => form.setData("country", e.target.value)}
                placeholder="US"
              />
            </div>

            <div className="space-y-1.5">
              <Label>City</Label>
              <Input
                value={form.data.city}
                onChange={(e) => form.setData("city", e.target.value)}
              />
            </div>

            <div className="space-y-1.5">
              <Label>State / Region</Label>
              <Input
                value={form.data.state}
                onChange={(e) => form.setData("state", e.target.value)}
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>Bio</Label>
            <Textarea
              rows={4}
              value={form.data.bio}
              onChange={(e) => form.setData("bio", e.target.value)}
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
            <Button type="submit" variant="gradient" disabled={form.processing}>
              {form.processing && <Loader2 className="h-4 w-4 animate-spin mr-1.5" />}
              Save Changes
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>

      <AlertDialog open={confirmEmailOpen} onOpenChange={setConfirmEmailOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Change this email?</AlertDialogTitle>
            <AlertDialogDescription>
              {member?.name || "This member"} will sign in with <strong>{newEmail}</strong> from now on,
              not {member?.email}. Check the new address for typos. A notice goes to the old address.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Go back</AlertDialogCancel>
            <AlertDialogAction onClick={save}>Change email</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  )
}
